// Refills the homepage archive tiles with a random set from the whole collection on every visit.
// Liquid renders the first tiles as the no-JS fallback (lazy images, so nothing loads twice when
// the swap lands before the band scrolls near). Shortly before the band nears the viewport, one
// small request pulls a random page of the collection's products.json and the tiles are reshuffled.
const PER_PAGE = 30;

class ArchiveShuffle extends HTMLElement {
  #observer = null;

  connectedCallback() {
    const count = Number(this.dataset.count) || 0;
    if (!this.dataset.url || count <= this.querySelectorAll('[data-archive-tile]').length) return;
    this.#observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.#observer?.disconnect();
      this.#shuffle(count);
    }, { rootMargin: '1200px 0px' });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #shuffle(count) {
    const tiles = Array.from(this.querySelectorAll('[data-archive-tile]'));
    const pages = Math.max(1, Math.ceil(count / PER_PAGE));
    const page = 1 + Math.floor(Math.random() * pages);
    let products = [];
    try {
      // products.json is Shopify's storefront JSON endpoint for a collection.
      const response = await fetch(`${this.dataset.url}/products.json?limit=${PER_PAGE}&page=${page}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) return;
      products = ((await response.json())?.products || []).filter((product) => product.images?.[0]?.src);
    } catch {
      return;
    }
    if (products.length < tiles.length) return;

    for (let i = products.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [products[i], products[j]] = [products[j], products[i]];
    }

    tiles.forEach((tile, index) => {
      const product = products[index];
      const url = `${this.dataset.url}/products/${product.handle}`;
      tile.querySelectorAll('a[href]').forEach((link) => link.setAttribute('href', url));
      tile.querySelectorAll('[aria-label]').forEach((node) => node.setAttribute('aria-label', product.title));
      const img = tile.querySelector('img');
      if (!img) return;
      const sized = (width) => {
        const src = new URL(product.images[0].src, window.location.href);
        src.searchParams.set('width', String(width));
        return src.toString();
      };
      img.srcset = `${sized(200)} 200w, ${sized(400)} 400w, ${sized(600)} 600w`;
      img.src = sized(400);
      img.alt = product.title;
    });
  }
}

if (!customElements.get('s-archive-shuffle')) customElements.define('s-archive-shuffle', ArchiveShuffle);
